import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import '../../styles/login.css';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Reset token is required');
    }
  }, [token]);

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (password.length > 128) {
      return 'Password must be no more than 128 characters';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError('Reset token is required');
      return;
    }

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    const validationError = validatePassword(newPassword);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/v1/owner/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          newPassword,
        }),
      });

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        setError('Invalid response from server');
        return;
      }

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setError(data.error || data.message || 'Failed to reset password');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            <h1 style={styles.title}>Invalid Reset Link</h1>
            <p style={styles.subtitle}>Reset token is required.</p>
          </div>
          <div style={styles.errorBox}>
            <p style={styles.errorText}>The reset link is missing or invalid.</p>
          </div>
          <button
            onClick={() => navigate('/forgot-password')}
            style={styles.button}
          >
            Request New Reset Link
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            <h1 style={styles.title}>Password Reset Successful</h1>
            <p style={styles.subtitle}>
              Your password has been reset. Redirecting to login...
            </p>
          </div>
          <div style={styles.successBox}>
            <p style={styles.successText}>
              You can now sign in with your new password.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.title}>Reset Password</h1>
          <p style={styles.subtitle}>
            Enter your new password below.
          </p>
        </div>

        {error && (
          <div 
            id="reset-error"
            role="alert"
            style={styles.error}
            aria-live="polite"
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.field}>
            <label htmlFor="newPassword" style={styles.label}>
              New Password
            </label>
            <div style={styles.passwordWrapper}>
              <input
                id="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={loading}
                autoComplete="new-password"
                style={{
                  ...styles.input,
                  ...styles.passwordInput,
                  ...(loading ? styles.inputDisabled : {}),
                }}
                minLength={12}
                aria-describedby={error ? 'reset-error' : 'password-help'}
                data-testid="owner-reset-password-input"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                disabled={loading}
                style={styles.passwordToggle}
                aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                tabIndex={0}
              >
                {showNewPassword ? (
                  <EyeOff size={20} style={styles.passwordToggleIcon} />
                ) : (
                  <Eye size={20} style={styles.passwordToggleIcon} />
                )}
              </button>
            </div>
            <p id="password-help" style={styles.helpText}>
              Must be at least 8 characters (max 128)
            </p>
          </div>

          <div style={styles.field}>
            <label htmlFor="confirmPassword" style={styles.label}>
              Confirm Password
            </label>
            <div style={styles.passwordWrapper}>
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                autoComplete="new-password"
                style={{
                  ...styles.input,
                  ...styles.passwordInput,
                  ...(loading ? styles.inputDisabled : {}),
                }}
                minLength={12}
                aria-describedby={error ? 'reset-error' : undefined}
                data-testid="owner-reset-confirm-password-input"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={loading}
                style={styles.passwordToggle}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                tabIndex={0}
              >
                {showConfirmPassword ? (
                  <EyeOff size={20} style={styles.passwordToggleIcon} />
                ) : (
                  <Eye size={20} style={styles.passwordToggleIcon} />
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || !newPassword || !confirmPassword}
            style={{
              ...styles.button,
              ...(loading ? styles.buttonDisabled : {}),
            }}
            aria-busy={loading}
            data-testid="owner-reset-password-submit-btn"
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <div style={styles.footer}>
          <button
            type="button"
            onClick={() => navigate('/login')}
            style={styles.linkButton}
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    width: '100%',
    margin: '0 auto',
    padding: 'var(--spacing-4)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    backgroundColor: 'var(--color-bg-primary, #ffffff)',
    padding: 'var(--spacing-8)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--elevation-3)',
    border: '1px solid var(--color-border-light, #e8e8ea)',
    width: '100%',
    maxWidth: '440px',
  },
  header: {
    textAlign: 'center',
    marginBottom: 'var(--spacing-6)',
  },
  title: {
    fontSize: 'var(--font-size-2xl)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    lineHeight: 'var(--line-height-tight-medium)',
    letterSpacing: 'var(--letter-spacing-tight-medium)',
    margin: 0,
    marginBottom: 'var(--spacing-2)',
  },
  subtitle: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    margin: 0,
    lineHeight: 'var(--line-height-relaxed)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-4)',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-1)',
  },
  label: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-secondary)',
    marginBottom: 'var(--spacing-1)',
  },
  input: {
    padding: 'var(--spacing-3) var(--spacing-4)',
    border: '1px solid var(--color-border-light, #e8e8ea)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-base)',
    fontFamily: 'var(--font-family-primary)',
    color: 'var(--color-text-primary)',
    backgroundColor: 'var(--color-bg-primary)',
    transition: 'border-color var(--transition-base), box-shadow var(--transition-base), background-color var(--transition-base)',
    outline: 'none',
    width: '100%',
  },
  inputDisabled: {
    backgroundColor: 'var(--color-bg-tertiary, #eceef0)',
    cursor: 'not-allowed',
    opacity: 0.7,
  },
  passwordWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  passwordInput: {
    paddingRight: '3rem', // Space for toggle button
  },
  passwordToggle: {
    position: 'absolute',
    right: 'var(--spacing-2)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 'var(--spacing-1)',
    color: 'var(--color-text-tertiary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color var(--transition-base), transform var(--transition-fast)',
    zIndex: 1,
    borderRadius: 'var(--radius-sm)',
  },
  passwordToggleIcon: {
    color: 'inherit',
    strokeWidth: 1.5,
  },
  helpText: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-tertiary)',
    marginTop: 'var(--spacing-1)',
    marginBottom: 0,
  },
  error: {
    padding: 'var(--spacing-3)',
    backgroundColor: 'var(--color-semantic-error-bg, rgba(239, 68, 68, 0.1))',
    color: 'var(--color-semantic-error-text, #991b1b)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-sm)',
    border: '1px solid var(--color-semantic-error-base, #ef4444)',
    lineHeight: 'var(--line-height-normal)',
    marginBottom: 'var(--spacing-4)',
  },
  errorBox: {
    padding: 'var(--spacing-3)',
    backgroundColor: 'var(--color-semantic-error-bg, rgba(239, 68, 68, 0.1))',
    color: 'var(--color-semantic-error-text, #991b1b)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-sm)',
    border: '1px solid var(--color-semantic-error-base, #ef4444)',
    lineHeight: 'var(--line-height-normal)',
    marginBottom: 'var(--spacing-4)',
  },
  errorText: {
    margin: 0,
  },
  successBox: {
    padding: 'var(--spacing-3)',
    backgroundColor: 'var(--color-semantic-success-bg, rgba(16, 185, 129, 0.1))',
    color: 'var(--color-semantic-success-text, #065f46)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-sm)',
    border: '1px solid var(--color-semantic-success-base, #10b981)',
    lineHeight: 'var(--line-height-normal)',
    marginBottom: 'var(--spacing-4)',
  },
  successText: {
    margin: 0,
  },
  button: {
    width: '100%',
    padding: 'var(--spacing-3) var(--spacing-4)',
    backgroundColor: 'var(--color-brand-primary, #0F4C75)',
    color: 'var(--color-text-inverse, #ffffff)',
    border: '1px solid var(--color-brand-primary, #0F4C75)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-semibold)',
    cursor: 'pointer',
    transition: 'background-color var(--transition-base), border-color var(--transition-base), transform var(--transition-fast)',
    outline: 'none',
    marginTop: 'var(--spacing-2)',
  },
  buttonDisabled: {
    backgroundColor: 'var(--color-text-disabled, #a3a3a3)',
    borderColor: 'var(--color-text-disabled, #a3a3a3)',
    cursor: 'not-allowed',
    transform: 'none',
  },
  footer: {
    marginTop: 'var(--spacing-6)',
    textAlign: 'center',
    paddingTop: 'var(--spacing-4)',
    borderTop: '1px solid var(--color-border-light, #e8e8ea)',
  },
  linkButton: {
    background: 'none',
    border: 'none',
    color: 'var(--color-brand-primary, #0F4C75)',
    cursor: 'pointer',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    textDecoration: 'none',
    padding: 0,
    transition: 'color var(--transition-base)',
  },
};
